import React from 'react';
import Moment from 'react-moment';
import moment from 'moment';
import { useHistory } from 'react-router-dom';
import useFetch from 'use-http';
import {
  Card,
  CardContent,
  CardHeader,
  CardFooter,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Heading } from '@/components/ui/shared/heading';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { SlashIcon, Download } from 'lucide-react';
import { Link } from 'react-router-dom/cjs/react-router-dom';

const VaccinationDetails = () => {
  const history = useHistory();
  const { get: getFile } = useFetch('/UserAccount/DownloadAmisFile');
  const vaccination = history.location.state?.data;

  const handleFileDownload = async () => {
    const res = await getFile(
      `?fileType=${vaccination.file.type}&fileGuid=${vaccination.file.guid}&category=Vaccination`,
    ).then((res) => {
      const base64Data = res;
      const blob = base64toBlob(base64Data, 'application/pdf');
      const downloadLink = document.createElement('a');
      downloadLink.href = URL.createObjectURL(blob);
      downloadLink.download =
        vaccination.name +
        ' ' +
        moment(new Date(vaccination.occurrenceDateTime)).format('YYYY-MM-DD') +
        '.pdf';

      document.body.appendChild(downloadLink);
      setTimeout(() => {
        downloadLink.click();
        document.body.removeChild(downloadLink);
      }, 120);
    });
  };

  function base64toBlob(base64Data, contentType = '') {
    const sliceSize = 1024;
    const byteCharacters = atob(base64Data);
    const bytesLength = byteCharacters.length;
    const slicesCount = Math.ceil(bytesLength / sliceSize);
    const byteArrays = new Array(slicesCount);

    for (let sliceIndex = 0; sliceIndex < slicesCount; ++sliceIndex) {
      const begin = sliceIndex * sliceSize;
      const end = Math.min(begin + sliceSize, bytesLength);

      const bytes = new Array(end - begin);
      for (let offset = begin, i = 0; offset < end; ++i, ++offset) {
        bytes[i] = byteCharacters[offset].charCodeAt(0);
      }
      byteArrays[sliceIndex] = new Uint8Array(bytes);
    }
    return new Blob(byteArrays, { type: contentType });
  }

  return (
    <div className="space-y-5">
      <Breadcrumb className="px-4">
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/userAccount/vaccinations">Skiepai</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator>
            <SlashIcon />
          </BreadcrumbSeparator>
          <BreadcrumbItem>
            <BreadcrumbPage>{vaccination.name}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      {/* Card 1: Vaccination Info */}
      <Card>
        <CardHeader>
          <Heading variant="userAccountDetailsTitle">{vaccination.name}</Heading>
        </CardHeader>
        <CardContent className="space-y-2">
          <div>
            <span>Data: </span>
            <span>
              {(new Date(Date.parse(vaccination.occurrenceDateTime)).getUTCHours() === 0 && (
                <Moment utc format="YYYY-MM-DD">
                  {Date.parse(vaccination.occurrenceDateTime)}
                </Moment>
              )) || (
                <Moment utc format="YYYY-MM-DD HH:mm">
                  {Date.parse(vaccination.occurrenceDateTime)}
                </Moment>
              )}
            </span>
          </div>

          {vaccination.practitioner?.name && (
            <div>
              <span>Gydytojas: </span>
              <span>
                {vaccination.practitioner?.qualification} - {vaccination.practitioner?.name}{' '}
                {vaccination.practitioner?.surname}
              </span>
            </div>
          )}

          {vaccination.diagnosis && (
            <div>
              <span>Pagrindinė diagnozė: </span>
              <span>{vaccination.diagnosis}</span>
            </div>
          )}

          {vaccination.targetDisease && (
            <div>
              <span>Liga: </span>
              <span>{vaccination.targetDisease}</span>
            </div>
          )}

          {vaccination.e063StatusDisplay.toLowerCase() !== 'pasirašytas' && (
            <div className="text-red-600 font-medium">LAUKIAMA REZULTATŲ</div>
          )}

          {vaccination.doseQuantity && (
            <div>
              <span>Dozė: </span>
              <span>{vaccination.doseQuantity}</span>
            </div>
          )}

          {vaccination.lotNumber && (
            <div>
              <span>Serijos numeris: </span>
              <span>{vaccination.lotNumber}</span>
            </div>
          )}

          {vaccination.vaccine && (
            <div>
              <span>Vakcinos pavadinimas: </span>
              <span>{vaccination.vaccine}</span>
            </div>
          )}

          {vaccination.manufacturer && (
            <div>
              <span>Gamintojas: </span>
              <span>{vaccination.manufacturer}</span>
            </div>
          )}

          {vaccination.nextImmunizationDate && (
            <div>
              <span>Kitos vakcinos data: </span>
              <span>
                {(new Date(Date.parse(vaccination.nextVaccinationDate)).getUTCHours() === 0 && (
                  <Moment utc format="YYYY-MM-DD">
                    {Date.parse(vaccination.nextImmunizationDate)}
                  </Moment>
                )) || (
                  <Moment utc format="YYYY-MM-DD HH:mm">
                    {Date.parse(vaccination.nextImmunizationDate)}
                  </Moment>
                )}
              </span>
            </div>
          )}
        </CardContent>
        <CardFooter />
      </Card>

      {/* Card 2: Dokumentai */}
      {vaccination.file?.guid && (
        <Card>
          <CardHeader>
            <Heading variant="userAccountDetailsTitle">Dokumentai</Heading>
          </CardHeader>
          <CardContent>
            <Button variant="light" onClick={handleFileDownload}>
              <Download />
              Atsisiųsti PDF
            </Button>
          </CardContent>
          <CardFooter />
        </Card>
      )}
    </div>
  );
};

export default VaccinationDetails;
